import React, { useState, useEffect, useCallback } from 'react';
import { Languages, RefreshCw, Loader2, ExternalLink, X, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANG_LABELS = { en: 'Englisch', fr: 'Französisch', it: 'Italienisch', es: 'Spanisch' };
const KEY_LABELS = {
  hero: 'Hero', products: 'Lösungen', services: 'Leistungen', projects: 'Projekte',
  testimonials: 'Kundenstimmen', pricing: 'Preise', legal_privacy: 'Datenschutz',
  legal_terms: 'AGB', legal_imprint: 'Impressum',
};

const ReviewModal = ({ detail, onClose, onSaved, authHeaders, onAuthError }) => {
  const [text, setText] = useState(JSON.stringify(detail.translation || {}, null, 2));
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setMessage('❌ Ungültiges JSON – bitte Format prüfen (Anführungszeichen, Kommas).');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/translations/${detail.key}/${detail.lang}`, { data: parsed }, { headers: authHeaders() });
      setMessage('✅ Übersetzung gespeichert.');
      onSaved();
    } catch (error) {
      if (!onAuthError(error)) setMessage('❌ Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1D1D1F]/40 backdrop-blur-md z-50 flex items-center justify-center p-6" onClick={onClose} data-testid="translation-review-modal">
      <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#1D1D1F]">
              Übersetzung prüfen: {KEY_LABELS[detail.key] || detail.key} – {LANG_LABELS[detail.lang]}
            </h3>
            {detail.stale && (
              <p className="text-xs text-amber-600 mt-1">⚠️ Veraltet – der deutsche Text wurde seither geändert.</p>
            )}
          </div>
          <button onClick={onClose} className="text-[#86868B] hover:text-[#1D1D1F]" data-testid="translation-review-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs font-semibold text-[#86868B] mb-2">Deutsch (Original)</p>
            <pre className="bg-[#F5F5F7] rounded-xl p-4 text-xs text-[#1D1D1F] whitespace-pre-wrap max-h-[50vh] overflow-y-auto">
              {JSON.stringify(detail.base, null, 2)}
            </pre>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#86868B] mb-2">
              {LANG_LABELS[detail.lang]} (bearbeitbar – nur Texte ändern, Struktur beibehalten)
            </p>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="bg-[#F5F5F7] border-0 rounded-xl text-xs font-mono h-[50vh]"
              data-testid="translation-review-textarea"
            />
          </div>
        </div>

        {message && <p className="text-sm text-[#1D1D1F] mb-3">{message}</p>}

        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-11 px-6"
          data-testid="translation-review-save"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Speichere…' : 'Korrektur speichern'}
        </Button>
      </div>
    </div>
  );
};

const TranslationsPanel = ({ authHeaders, onAuthError }) => {
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [reviewKey, setReviewKey] = useState('hero');
  const [reviewLang, setReviewLang] = useState('en');
  const [reviewDetail, setReviewDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/translations/status`, { headers: authHeaders() });
      setStatus(res.data);
    } catch (error) {
      if (!onAuthError(error)) console.error('Error loading translation status:', error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const running = status?.job?.status === 'running';

  useEffect(() => {
    if (!running) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [running, load]);

  const rebuild = async (force) => {
    setMessage('');
    try {
      const res = await axios.post(`${API}/admin/translations/rebuild`, { force }, { headers: authHeaders() });
      if (res.data.success) {
        setMessage('Übersetzung gestartet – läuft im Hintergrund…');
        setTimeout(load, 1500);
      } else {
        setMessage(res.data.message || 'Übersetzung läuft bereits.');
      }
    } catch (error) {
      if (!onAuthError(error)) setMessage('Fehler beim Starten der Übersetzung');
    }
  };

  const openReview = async () => {
    setMessage('');
    try {
      const res = await axios.get(`${API}/admin/translations/${reviewKey}/${reviewLang}`, { headers: authHeaders() });
      if (!res.data.translation) {
        setMessage('Für diesen Bereich existiert noch keine Übersetzung in dieser Sprache.');
        return;
      }
      setReviewDetail(res.data);
    } catch (error) {
      if (!onAuthError(error)) setMessage('Übersetzung konnte nicht geladen werden');
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 mb-6" data-testid="translations-panel">
      <div className="flex items-center gap-2 mb-2">
        <Languages className="w-5 h-5 text-[#0071E3]" />
        <h3 className="text-lg font-semibold text-[#1D1D1F]">Übersetzungen (EN / FR / IT / ES)</h3>
      </div>
      <p className="text-sm text-[#86868B] mb-1">
        Sie pflegen die Inhalte auf Deutsch – die KI übersetzt automatisch.
        Nach jedem Speichern wird der geänderte Bereich im Hintergrund neu übersetzt.
      </p>
      <p className="text-xs text-[#86868B] mb-4">
        Hinweis: Der Bereich «Lösungen» wird nur in EN/FR/IT übersetzt (Spanisch zeigt Deutsch).
      </p>

      {status && (
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(status.languages || {}).map(([lang, info]) => {
            const ok = info.ok === info.total;
            return (
              <div
                key={lang}
                className={`rounded-xl px-4 py-2 text-sm ${ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}
                data-testid={`translation-status-${lang}`}
              >
                <span className="font-semibold">{LANG_LABELS[lang] || lang}</span>
                {': '}{info.ok}/{info.total} aktuell
                {info.stale > 0 && ` · ${info.stale} veraltet`}
                {info.missing > 0 && ` · ${info.missing} fehlend`}
              </div>
            );
          })}
        </div>
      )}

      {running && (
        <div className="flex items-center gap-2 text-sm text-[#0071E3] mb-4" data-testid="translation-job-running">
          <Loader2 className="w-4 h-4 animate-spin" />
          Übersetzung läuft im Hintergrund… (Status aktualisiert sich automatisch)
        </div>
      )}
      {!running && status?.job?.status === 'done' && status.job.results && (
        <p className="text-xs text-[#86868B] mb-4">
          Letzter Lauf: {status.job.results.translated} übersetzt, {status.job.results.skipped} aktuell
          {status.job.results.failed?.length > 0 && `, ${status.job.results.failed.length} fehlgeschlagen`}
        </p>
      )}

      <div className="flex flex-wrap gap-3 mb-5">
        <Button
          onClick={() => rebuild(false)}
          disabled={running}
          className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-10 px-6 disabled:opacity-50"
          data-testid="translations-rebuild-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Fehlende & veraltete übersetzen
        </Button>
        <Button
          onClick={() => rebuild(true)}
          disabled={running}
          variant="outline"
          className="border-[#D2D2D7] text-[#1D1D1F] rounded-full h-10 px-6 disabled:opacity-50"
          data-testid="translations-force-button"
        >
          Alles neu übersetzen
        </Button>
      </div>

      <div className="border-t border-[#D2D2D7] pt-4">
        <p className="text-sm font-medium text-[#1D1D1F] mb-3">Übersetzung prüfen & korrigieren</p>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={reviewKey}
            onChange={(e) => setReviewKey(e.target.value)}
            className="bg-[#F5F5F7] border-0 rounded-xl h-10 px-3 text-sm text-[#1D1D1F]"
            data-testid="translation-review-key"
          >
            {(status?.keys || Object.keys(KEY_LABELS)).map((k) => (
              <option key={k} value={k}>{KEY_LABELS[k] || k}</option>
            ))}
          </select>
          <select
            value={reviewLang}
            onChange={(e) => setReviewLang(e.target.value)}
            className="bg-[#F5F5F7] border-0 rounded-xl h-10 px-3 text-sm text-[#1D1D1F]"
            data-testid="translation-review-lang"
          >
            {Object.entries(LANG_LABELS).map(([l, label]) => (
              <option key={l} value={l}>{label}</option>
            ))}
          </select>
          <Button
            onClick={openReview}
            variant="outline"
            className="border-[#D2D2D7] text-[#1D1D1F] rounded-full h-10 px-5 text-sm"
            data-testid="translation-review-open"
          >
            Prüfen
          </Button>
          <span className="text-xs text-[#86868B]">Vorschau:</span>
          {['en', 'fr', 'it', 'es'].map((l) => (
            <a
              key={l}
              href={`/?lang=${l}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[#0071E3] hover:underline inline-flex items-center gap-0.5"
              data-testid={`translation-preview-${l}`}
            >
              {l.toUpperCase()}<ExternalLink className="w-3 h-3" />
            </a>
          ))}
        </div>
      </div>

      {message && <p className="text-sm text-[#0071E3] mt-3" data-testid="translations-message">{message}</p>}

      {reviewDetail && (
        <ReviewModal
          detail={reviewDetail}
          onClose={() => setReviewDetail(null)}
          onSaved={load}
          authHeaders={authHeaders}
          onAuthError={onAuthError}
        />
      )}
    </div>
  );
};

export default TranslationsPanel;
